import type { ApiClient, Schemas } from '@argzombie/api-client';
import { createContext, useContext } from 'react';
import { useApi } from './api';

/**
 * Characters on the player detail (ADR 0069): a character is a run row; a dead run that isn't deleted is a **zombie**.
 * The detail (`GET /admin/players/{id}`) carries `characters: CharacterView[]`; picking a row sets the `runId` every
 * run-scoped section reads with (`?runId=` on `/admin/players/{id}/...`, default the active character).
 * The types are the api-client's (shared-cs `ArgZombie.Shared.Characters`).
 */

/** shared-cs `CharacterStatus`: the wire words. The DB's `dead` reads as `zombie`. */
export type CharacterStatus = 'alive' | 'zombie';

/** shared-cs `CharacterView` (camelCase, Unix-ms longs); `status` is a `CharacterStatus` word. */
export type CharacterView = Schemas['CharacterView'];

/** `AdminPlayerDetail` with ADR 0069's `characters`. */
export type AdminPlayerDetail = Schemas['AdminPlayerDetail'];

/** `AdminRaidRow` with the raid's character (`runId`, `slot`; ADR 0069). */
export type AdminRaidRow = Schemas['AdminRaidRow'];

/** A run status as admin text: the API's legacy `dead` is a zombie now. */
export const statusWord = (status: string | null | undefined): string =>
  status === 'dead' ? 'zombie' : (status ?? '');

export const isZombie = (status: string | null | undefined) =>
  status === 'zombie' || status === 'dead';

/** "Mara" or "Character 2" when the run has no name yet. */
export const characterName = (c: Pick<CharacterView, 'name' | 'slot'> | null | undefined) =>
  c?.name?.trim() || (c ? `Character ${c.slot}` : 'the character');

/** "Mara (slot 2, run #5)". */
export const characterLabel = (c: CharacterView) =>
  `${characterName(c)} (slot ${c.slot}, run #${c.number})`;

/** The row a detail starts on: the active character, else the first. */
export const defaultCharacter = (list: CharacterView[] | null | undefined) =>
  list?.find((c) => c.active) ?? list?.[0];

/** Paths a selected run applies to: the player's sub-reads and actions, and the skill explain. */
const RUN_SCOPED = /^\/admin\/players\/\{id\}|^\/dev\/skills\/explain$/;

type Init = { params?: { query?: Record<string, unknown> } & Record<string, unknown> } & Record<
  string,
  unknown
>;

/**
 * The client with `?runId=` added to every run-scoped call (see `RUN_SCOPED`). No run id = the client itself (the API
 * then uses the active character). Unknown query params are ignored by an older API.
 * The API reads `?runId=` in middleware (`Infrastructure/RunScope`), so the OpenAPI spec doesn't type it: this is the one place
 * that adds it.
 */
export function withRunId(client: ApiClient, runId: string | null | undefined): ApiClient {
  if (!runId) return client;
  type Call = (path: string, init?: Init) => unknown;
  const wrap =
    (call: Call) =>
    (path: string, init: Init = {}) =>
      RUN_SCOPED.test(path)
        ? call(path, {
            ...init,
            params: { ...init.params, query: { ...init.params?.query, runId } },
          })
        : call(path, init);
  const c = client as unknown as { GET: Call; POST: Call; PUT: Call; DELETE: Call; PATCH: Call };
  return {
    ...client,
    GET: wrap(c.GET),
    POST: wrap(c.POST),
    PUT: wrap(c.PUT),
    DELETE: wrap(c.DELETE),
    PATCH: wrap(c.PATCH),
  } as unknown as ApiClient;
}

/** The selected character on a player detail. */
export type RunScope = { runId?: string; character?: CharacterView };

export const RunScopeContext = createContext<RunScope>({});

/**
 * The selected character, and a client that reads its run. Outside a player detail (or with an older API) `runId` is
 * undefined and the client is the plain one. Put `runId` last in run-scoped query keys so switching refetches.
 */
export function useRunScope(): RunScope & { client: ApiClient } {
  const { client } = useApi();
  const scope = useContext(RunScopeContext);
  return { ...scope, client: withRunId(client, scope.runId) };
}
